import { Box, Flex, Menu, MenuButton, MenuItem, MenuList, Portal, Text } from '@chakra-ui/react';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import React from 'react';

import type { TransactionItem } from '../../interface/transactions';
import {
  CARD_BG,
  DRAG_TARGET_LINE,
  INK_MUTED,
  TX_BORDER,
  TX_DROP_LINE_H,
  TX_DROP_LINE_OFFSET,
  TX_GRIP_PAD,
  TX_GRIP_RADIUS,
  TX_LABEL_COLOR,
  TX_LABEL_SIZE,
  TX_LABEL_SPACING,
  TX_LABEL_WEIGHT,
  TX_MENU_FONT_SIZE,
  TX_MENU_MIN_W,
  TX_MENU_Z,
  TX_NEEDS_PROCEDURE_BORDER,
  TX_PAD_X,
  TX_PAD_Y,
  TX_RADIUS,
  TX_RULE,
  TX_TOPLINE_GAP,
  TX_TOPLINE_MB
} from '../../styles/tokens';

export type TransactionMove = 'top' | 'up' | 'down' | 'bottom';

export type TransactionContainerProps = {
  readonly index: number;
  readonly transaction: TransactionItem;
  readonly grip?: 'active' | 'inert';
  readonly moves?: readonly TransactionMove[];
  readonly onMove?: (move: TransactionMove) => void;
  readonly dropIndicator?: 'before' | 'after';
  readonly onGripDragStart?: (event: React.DragEvent) => void;
  readonly onDragEnd?: (event: React.DragEvent) => void;
  readonly onDragOver?: (event: React.DragEvent) => void;
  readonly onDrop?: (event: React.DragEvent) => void;
  readonly children?: React.ReactNode;
};

const MOVE_LABELS: Readonly<Record<TransactionMove, string>> = {
  top: 'Move to Top',
  up: 'Move Up',
  down: 'Move Down',
  bottom: 'Move to Bottom'
};

const GRIP_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  color: INK_MUTED,
  p: TX_GRIP_PAD,
  borderRadius: TX_GRIP_RADIUS,
  bg: 'transparent',
  border: 'none'
} as const;

export const TransactionContainer: React.FC<TransactionContainerProps> = ({
  index,
  transaction,
  grip = 'inert',
  moves = [],
  onMove,
  dropIndicator,
  onGripDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  children
}) => {
  const number = index + 1;
  return (
    <Box
      role="group"
      aria-label={`Transaction ${number}`}
      data-transaction-id={transaction.id}
      data-transaction-index={index}
      data-needs-procedure={transaction.needsProcedure ? 'true' : undefined}
      data-drop-indicator={dropIndicator}
      position="relative"
      bg={CARD_BG}
      borderWidth="1px"
      borderStyle={transaction.needsProcedure ? 'dashed' : 'solid'}
      borderColor={transaction.needsProcedure ? TX_NEEDS_PROCEDURE_BORDER : TX_BORDER}
      borderRadius={TX_RADIUS}
      px={TX_PAD_X}
      py={TX_PAD_Y}
      onDragOver={onDragOver}
      onDrop={onDrop}
      _before={
        dropIndicator
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              height: TX_DROP_LINE_H,
              bg: DRAG_TARGET_LINE,
              top: dropIndicator === 'before' ? TX_DROP_LINE_OFFSET : undefined,
              bottom: dropIndicator === 'after' ? TX_DROP_LINE_OFFSET : undefined
            }
          : undefined
      }
    >
      <Flex align="center" gap={TX_TOPLINE_GAP} mb={children != null ? TX_TOPLINE_MB : undefined}>
        {grip === 'active' ? (
          <Menu placement="bottom-start" isLazy>
            <MenuButton
              type="button"
              aria-label="Reorder transaction"
              title="Drag to reorder, or click for move options"
              draggable
              onDragStart={onGripDragStart}
              onDragEnd={onDragEnd}
              cursor="grab"
              {...GRIP_STYLE}
            >
              <BiomeIcon iconName={IconName.Gripper} />
            </MenuButton>
            <Portal>
              <MenuList zIndex={TX_MENU_Z} minW={TX_MENU_MIN_W} fontSize={TX_MENU_FONT_SIZE}>
                {moves.map((move) => (
                  <MenuItem key={move} onClick={() => onMove?.(move)}>
                    {MOVE_LABELS[move]}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        ) : (
          <Box
            as="span"
            role="button"
            aria-label="Reorder transaction"
            aria-disabled="true"
            tabIndex={-1}
            title="Reordering is not available"
            cursor="not-allowed"
            {...GRIP_STYLE}
          >
            <BiomeIcon iconName={IconName.Gripper} />
          </Box>
        )}
        <Text
          as="span"
          fontSize={TX_LABEL_SIZE}
          fontWeight={TX_LABEL_WEIGHT}
          letterSpacing={TX_LABEL_SPACING}
          color={TX_LABEL_COLOR}
        >
          {`TX ${number}`}
        </Text>
        <Box flex="1" h="1px" bg={TX_RULE} aria-hidden="true" data-testid="transaction-separator" />
      </Flex>
      {children != null && <Box data-testid="transaction-body">{children}</Box>}
    </Box>
  );
};
